/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BusinessSettingsModal} from '../business-settings-modal';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {SupplyingPackingPlantModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/supplying-packing-plant.model';
import {TooltipEnum} from '../../../../shared/tooltip/tooltip.enum';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {ValidatorFn, Validators} from '@angular/forms';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {InputListModel} from '../input-list.model';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';
import {take} from 'rxjs/operators';
import {combineLatest, Observable, of, timer} from 'rxjs';
import {BusinessSettingsApi} from '../../../../api/ms-settings/business/schema/v1/business-settings.api';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {InputDropdownComponent} from '../../../../shared/input/input-dropdown/input-dropdown.component';
import {InputComponent} from '../../../../shared/input/input/input.component';
import {IntegrationChannelModel} from '../../../../api/ms-settings/business/schema/v1/contracts/integration-channel.model';

@Component({
    selector: 'app-supplying-packing-plant-modal',
    templateUrl: './supplying-packing-plant-modal.component.html',
    styleUrls: ['../business-settings-modal.css', './supplying-packing-plant-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SupplyingPackingPlantModalComponent extends BusinessSettingsModal
        <BusinessSettingsModalState<SupplyingPackingPlantModel>> {

    @ViewChild('plantName') plantNameInput: InputTextComponent;
    @ViewChild('plantCode') plantCodeInput: InputTextComponent;
    @ViewChild('plantNumber') plantNumberInput: InputTextComponent;
    @ViewChild('sapSystem') sapSystemInput: InputDropdownComponent;

    readonly EDIT_TITLE = 'Edit a supplying packing plant';
    readonly ADD_TITLE = 'Add a supplying packing plant';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';
    readonly ENTITY_NAME = 'supplying packing plant';

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,100}$/)],
        sppCode: [Validators.pattern(/^\w{4}$/)],
        sppNumber: [Validators.pattern(/^00\d{8}$/)]
    };

    TooltipEnum = TooltipEnum;

    public plantNameMessages = new Map<WarningsEnum, string[]>();
    public plantCodeMessages = new Map<WarningsEnum, string[]>();
    public plantNumberMessages = new Map<WarningsEnum, string[]>();
    public integrationChannels: IntegrationChannelModel[];

    constructor(public typeahead: TypeaheadHelperService, inputMessages: InputMessagesService, private settingsApi: BusinessSettingsApi) {
        super(inputMessages);
    }

    getInitialData = (): Observable<any[]> => {
        this.settingsApi.getIntegrationChannels()
.subscribe((integrationChannels: IntegrationChannelModel[]) => {
            this.integrationChannels = integrationChannels;
        });
        return of(this.integrationChannels);
    };

    setEntity(element: EntityElementModel<SupplyingPackingPlantModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: element.currentValue
        });

        combineLatest([this.settingsApi.getIntegrationChannels(), this.onReady])
                .pipe(take(1))
                .subscribe(([integrationChannels, isReady]: [IntegrationChannelModel[], boolean]) => {
                    timer(0)
.subscribe(() => {
                        this.plantNameInput.setValue(this.state.currentValue.name);
                        this.plantCodeInput.setValue(this.state.currentValue.code);
                        this.plantNumberInput.setValue(this.state.currentValue.number);
                        if (this.isEdit && this.state.currentValue.integrationChannelCode != null) {
                            const integrationChannelCodeValue = this.state.currentValue.integrationChannelCode.toString();
                            const setOldIntegrationChannelCode =
                                    integrationChannels.find((item) => item.code === integrationChannelCodeValue);
                            this.sapSystemInput.setValue(setOldIntegrationChannelCode);
                        }
                        this.setActiveInputState(element.active, true);
                    });
                });

    }

    plantNameInputChange(name: string): void {
        this.inputMessages.mandatoryWarnings(this.plantNameInput.inputControl, this.plantNameMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(this.plantNameInput.inputControl, this.plantNameMessages, this.plantNameInput.label);
        this.plantNameMessages = new Map(this.plantNameMessages);
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: name
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0)
            }
        });
        this.setModalValidity();
    }

    plantCodeInputChange(code: string): void {
        const codeInUpperCase = code != null ? code.toUpperCase() : null;

        this.inputMessages.mandatoryWarnings(this.plantCodeInput.inputControl, this.plantCodeMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.plantCodeInput.inputControl,
                this.plantCodeMessages,
                this.plantCodeInput.label,
                'A167'
        );

        this.plantCodeMessages = new Map(this.plantCodeMessages);

        const errors = this.plantCodeMessages.get(WarningsEnum.ERROR);

        if (errors.length === 0) {
            this.manageAlreadyExistsCode(codeInUpperCase, this.ENTITY_NAME, this.plantCodeMessages, this.plantCodeInput);
        }

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: codeInUpperCase
            },
            invalid: {
                ...this.state.invalid,
                code: (code == null || code.length === 0) || (errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    plantNumberInputChange(plantNumber: string): void {
        this.manageAlreadyExistsNumber(plantNumber, this.ENTITY_NAME, this.plantNumberMessages, this.plantNumberInput);
        this.inputMessages.mandatoryWarnings(this.plantNumberInput.inputControl, this.plantNumberMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.plantNumberInput.inputControl,
                this.plantNumberMessages,
                this.plantNumberInput.label,
                '0084922938'
        );
        this.plantNumberMessages = new Map(this.plantNumberMessages);
        const errors = this.plantNumberMessages.get(WarningsEnum.ERROR);
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                number: plantNumber
            },
            invalid: {
                ...this.state.invalid,
                number: (plantNumber == null || (errors != null && errors.length > 0))
            }
        });

        this.setModalValidity();
    }

    integrationChannelsInputChange(integrationChannel: IntegrationChannelModel): void {
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                integrationChannelCode: integrationChannel != null && integrationChannel.code != null ?
                        integrationChannel.code : integrationChannel
            },
            invalid: {
                ...this.state.invalid,
                integrationChannelCode: integrationChannel == null
            }
        });
        this.setModalValidity();
    }

    manageAlreadyExistsNumber(number: string, entity: string, messagesMap: Map<WarningsEnum, string[]>,
                              input: InputComponent): void {

        let usedCodes = this.excludedEntities.map((e) => e.currentValue.number);

        if (this.isEdit) {
            usedCodes = usedCodes.filter((e) => e !== this.state.originalElement.currentValue.number);
        }

        if (number != null && usedCodes.includes(number)) {
            messagesMap.set(WarningsEnum.ERROR, [`That ${entity} number is already in use`]);
            input.markAsInvalid();
        } else {
            messagesMap.delete(WarningsEnum.ERROR);
        }
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.plantNameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.plantCodeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.plantNumberInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.sapSystemInput,
            type: InputTypeEnum.DROPDOWN,
            mandatory: true
        }];
    }
}
